import { TableHeader } from 'components/organisms/table';
import { useCallback, useEffect, useState } from 'react';
import styles from '../../../../pages/intermediary/intermediary.module.scss';
import { clientTableHeaderData } from './client-table.function';
import { ClientTableRow } from './ClientTableRow';
import { debounce } from 'lodash';
import { ClientType } from 'modules/dashboard/types/widgets/widget.types';
import {
  ITableSortLabel,
  Search,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableSortLabel
} from '@sanlam-glacier/app-common/components';
import { TableProps } from 'modules/dashboard/hooks/TableRenderer';

export const ClientTable = ({ data }: TableProps) => {
  const [order, setOrder] = useState<ITableSortLabel['direction']>(TableSortLabel.DIRECTION.DESC);
  const [orderBy, setOrderBy] = useState<keyof ClientType>('id');
  const [sortedData, setSortedData] = useState<ClientType[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState<ClientType[]>([]);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);

  const debouncedHandleSearch = useCallback(
    debounce((value: string) => {
      setSearchTerm(value);
    }, 500),
    []
  );

  useEffect(() => {
    const searchResults = sortedData.filter((row) => {
      const clientNameLower = row.clientName.toLowerCase();
      const searchTermLower = searchTerm.toLowerCase();
      return clientNameLower.includes(searchTermLower);
    });
    setFilteredData(searchResults);
  }, [searchTerm, sortedData]);

  const handleSort = (propertyName: keyof ClientType | undefined) => {
    if (!propertyName) return;
    const newOrder =
      propertyName === orderBy
        ? order === TableSortLabel.DIRECTION.ASC
          ? TableSortLabel.DIRECTION.DESC
          : TableSortLabel.DIRECTION.ASC
        : TableSortLabel.DIRECTION.DESC;
    setOrder(newOrder);
    setOrderBy(propertyName);
  };

  useEffect(() => {
    const sorted = [...((data || []) as ClientType[])].sort((a: ClientType, b: ClientType) => {
      const valueA = a[orderBy];
      const valueB = b[orderBy];
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return order === TableSortLabel.DIRECTION.ASC
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return order === TableSortLabel.DIRECTION.ASC ? valueA - valueB : valueB - valueA;
      }
      return 0;
    });
    setSortedData(sorted);
  }, [order, orderBy, data]);

  const paginatedData = filteredData.slice(page * pageSize, page * pageSize + pageSize).reverse();

  return (
    <>
      <div>
        <Search
          onChange={(value: string) => debouncedHandleSearch(value)}
          placeholder='Search clients'
        />

        <Table>
          <TableHeader
            headers={clientTableHeaderData}
            orderBy={orderBy}
            align={TableCell.ALIGN.LEFT}
            direction={order}
            handleSort={(propertyName) => handleSort(propertyName as keyof ClientType)}
            tableHeaderClassName={styles.tableSortHeaderLabel}
          />
          <TableBody>
            {paginatedData.map((row) => (
              <ClientTableRow key={row.id} row={row} />
            ))}
          </TableBody>
        </Table>
        <TablePagination
          className={styles.tableFooter}
          count={sortedData.length}
          rowsPerPage={pageSize}
          page={page}
          onPageChange={(newPage: number) => setPage(newPage)}
          onRowsPerPageChange={(newPageSize: number) => {
            setPage(0);
            setPageSize(newPageSize);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          closeMenuOnSelection
        />
      </div>
    </>
  );
};
